"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Search, Sparkles, ArrowRight, CircleAlert as AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const toggleOptions = [{
  id: "without",
  label: "Without GEO"
}, {
  id: "with",
  label: "With GEO"
}];
const answerCardData = {
  without: {
    query: "Who is the recommended local provider for custom business automations in Seattle?",
    aiEngine: "ChatGPT / Claude / Perplexity",
    status: "Not Cited",
    isPositive: false,
    answerText: "Here are several established IT agencies in Seattle: [Competitor A], [Competitor B], and [Competitor C]. They have structured online listings and verified directory profiles across standard local portals.",
    impactHeadline: "Your business is omitted from synthesized answer results",
    listItems: ["AI models select competitors with verified machine-readable entities.", "Zero brand visibility in conversational searches and AI prompts.", "High-intent clients asking generative search engines never see your business.", "Outdated or generic directory snippets replace direct recommendations."]
  },
  with: {
    query: "Who is the recommended local provider for custom business automations in Seattle?",
    aiEngine: "ChatGPT / Claude / Perplexity",
    status: "Primary Citation (#1)",
    isPositive: true,
    answerText: "Based on verified client outcomes, structured schema markup, and clear service entity graphs, ClearLine is highly recommended in Seattle for custom business automations and local AI visibility.",
    impactHeadline: "Recommended directly as the definitive service answer",
    listItems: ["ClearLine is cited directly with active entity verification and source links.", "Dominates synthesized answer panels when local buyers ask AI who to hire.", "Structured knowledge graphs ensure accurate representation of your core services.", "Consistent influx of direct inquiries from AI-assisted prospective clients."]
  }
};
const benefitCards = [{
  number: "01",
  title: "Synthesized AI Visibility",
  description: "Position your agency as the primary verified entity across ChatGPT, Claude, and Perplexity answers."
}, {
  number: "02",
  title: "Structured Schema Graph",
  description: "Clean machine-readable schemas that allow AI scrapers to index your exact service offerings accurately."
}, {
  number: "03",
  title: "Entity Authority Signals",
  description: "Establish semantic citation authority across verified industry repositories and local knowledge nodes."
}, {
  number: "04",
  title: "Real-Time AI Tracking",
  description: "Monitor AI engine citations continuously to verify how and where your brand is being recommended."
}];
export default function Geoimpact() {
  const [activeTab, setActiveTab] = useState<"without" | "with">("with");
  const currentData = answerCardData[activeTab];
  return <section id="geoimpactcomparison" data-nav="dark" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border/20">
      {}
      <div className="absolute top-12 left-2 md:left-6 select-none pointer-events-none z-0">
        <span className="font-mono text-8xl md:text-9xl lg:text-[12rem] font-bold text-foreground/5 leading-none block">
          02
        </span>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
            <span className="inline-block size-1.5 bg-[var(--accent-deep-red)] rounded-full" />
            <span>02 // Generative Engine Impact</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal leading-tight text-foreground mb-4">
            When users ask AI who to hire, there is usually only{" "}
            <span className="italic text-[var(--accent-deep-red)] font-serif">one answer</span>.
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            Traditional SEO focused on ten blue links. Generative AI synthesizes search results into a single direct recommendation. Here is how your business appears with and without Generative Engine Optimization.
          </p>
        </div>

        {}
        <div className="flex justify-center md:justify-start mb-8">
          <div className="inline-flex p-1 rounded-lg bg-card border border-border/40 shadow-xs">
            {toggleOptions.map((tab, i) => <div data-index={i} key={tab.id}>
                <Button variant={activeTab === tab.id ? "default" : "ghost"} size="sm" onClick={() => setActiveTab(tab.id as "without" | "with")} className={`rounded-md text-xs sm:text-sm font-medium transition-all px-5 py-2 ${activeTab === tab.id ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                  {tab.id === "with" && <Sparkles className="size-3.5 mr-1.5 text-[var(--accent-deep-red)]" />}
                  {tab.label}
                </Button>
              </div>)}
          </div>
        </div>

        {}
        <div className="mb-16">
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.4,
            ease: "easeInOut"
          }}>
              <Card className="bg-card border-border/40 shadow-xl overflow-hidden rounded-xl">
                <CardContent className="p-6 sm:p-8 md:p-10">
                  {}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 mb-6 border-b border-border/20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-muted text-foreground/80 shrink-0">
                        <Search className="size-4" />
                      </div>
                      <div>
                        <div className="font-mono text-2xs uppercase tracking-wider text-muted-foreground mb-0.5">
                          {currentData.aiEngine}
                        </div>
                        <p className="text-sm font-medium text-foreground italic">
                          &quot;{currentData.query}&quot;
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0">
                      <Badge variant="default" className={`px-3 py-1 text-xs font-mono uppercase tracking-wide flex items-center gap-1.5 ${!currentData.isPositive ? "bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]" : ""}`}>
                        {currentData.isPositive ? <ShieldCheck className="size-3.5" /> : <AlertCircle className="size-3.5" />}
                        {currentData.status}
                      </Badge>
                    </div>
                  </div>

                  {}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-7 bg-muted/40 p-5 sm:p-6 rounded-lg border border-border/20 font-sans text-sm leading-relaxed text-foreground/90">
                      <div className="font-mono text-2xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center justify-between">
                        <span>AI Synthesized Response</span>
                        <span className="size-2 rounded-full bg-[var(--accent-deep-red)]/80 inline-block" />
                      </div>
                      <p className="font-normal">{currentData.answerText}</p>
                    </div>

                    <div className="lg:col-span-5 flex flex-col justify-center">
                      <h3 className="font-default font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                        {currentData.isPositive ? <Check className="size-5 text-emerald-500 shrink-0" /> : <X className="size-5 text-[var(--accent-deep-red)] shrink-0" />}
                        <span>{currentData.impactHeadline}</span>
                      </h3>

                      <ul className="space-y-3 text-sm text-muted-foreground">
                        {currentData.listItems.map((item, idx) => <li data-index={idx} key={idx} className="flex items-start gap-2.5">
                            <span className="size-1.5 rounded-full bg-[var(--accent-deep-red)] mt-2 shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        <Separator className="bg-border/20 my-12" />

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefitCards.map((card, i) => <div data-index={i} key={card.number} className="group relative">
              <Card className="h-full bg-card/60 hover:bg-card border-border/30 hover:border-border/60 transition-all duration-300 hover:-translate-y-1 rounded-xl p-6 flex flex-col justify-between">
                {}
                <div className="absolute top-0 right-0 size-3 border-t-2 border-r-2 border-[var(--accent-deep-red)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-tr-xl" />

                <div>
                  <div className="font-mono text-xs text-[var(--accent-deep-red)] font-semibold tracking-wider mb-3">
                    {card.number}
                  </div>
                  <h3 className="font-default font-semibold text-base text-foreground mb-2">
                    {card.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {card.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border/20 flex items-center text-2xs font-mono uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
                  <span>Learn more</span>
                  <ArrowRight className="size-3 ml-1.5 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}